<template>
  <div>
    <PageHeader :title="pageTitle" class="px-2" enable-back-link/>
    <div class="upload-container">
      <div v-if="showUploadFormatsCard" class="upload-formats-card">
        <button type="button" class="upload-formats-close" aria-label="Bezárás" @click="hideUploadFormatsCard">
          <i class="fal fa-times"></i>
        </button>
        <div class="upload-formats-title">Feltölthető fájlformátumok</div>
        <div class="upload-formats-content">
          <div class="upload-formats-column">
            <div class="upload-formats-group">
              <div class="upload-formats-label">Fotók és képek:</div>
              <div class="upload-formats-values">JPEG, JPG, JFIF, TIF, TIFF, HEIC, BMP</div>
            </div>
            <div class="upload-formats-group">
              <div class="upload-formats-label">Dokumentumok:</div>
              <div class="upload-formats-values">PDF, DOC, DOCX, RTF, TXT, CSV, XLS, XLSX</div>
            </div>
          </div>
          <div class="upload-formats-column">
            <div class="upload-formats-group">
              <div class="upload-formats-label">Speciális formátumok:</div>
              <div class="upload-formats-values">XML</div>
            </div>
            <div class="upload-formats-group">
              <div class="upload-formats-label">Tömörített fájlok:</div>
              <div class="upload-formats-values">ZIP, 7Z</div>
            </div>
          </div>
        </div>
        <a
            class="upload-formats-info"
            href="https://acounto.com/file-feltoltes/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Feltölthető fájlformátumok részletei"
        >
          <i class="fal fa-info"></i>
        </a>
      </div>
      <div
          class="file-upload-page"
          :class="{'file-upload-page-empty': files.length === 0}"
          @dragover.prevent
          @drop.prevent="handleDroppedFiles"
      >
        <input
            ref="fileInput"
            type="file"
            multiple
            :accept="acceptedFileExtensions"
            style="display: none"
            @change="fileUploaderChange"
        />

        <div
            class="drop-area justify-content-around"
            :class="{'drop-area-empty': files.length === 0}"
            @click="$refs.fileInput.click()"
        >
          <i class="fal fa-upload drop-icon"></i>
          <div class="drop-content">
            <div class="drop-title">Válassz egy fájlt vagy húzd ide</div>
            <div class="drop-subtitle">Az XLS, DOC, DOCX, RTF és TXT fájlok mérete nem haladhatja meg a 7MB-ot</div>
          </div>
          <b-button variant="outline-secondary" class="pick-btn font-jumperlight">
            {{ files.length === 0 ? 'Fájlok kiválasztása' : 'További fájlok' }}
          </b-button>
        </div>
      </div>
    </div>

      <div v-if="files.length > 0" class="file-list-wrapper">
        <div class="actions-row mb-5">
          <div
              v-if="showUploadCounter"
              :key="isUploading ? 'upload-counter-uploading' : 'upload-counter-complete'"
              class="upload-counter"
              :class="{'upload-counter-complete': hasUploadFinished}"
          >
            <template v-if="isUploading">
              <i class="fal fa-spinner-third fa-spin upload-counter-loader"></i>
              <span>Feltöltés alatt: {{ uploadedFilesCount }}/{{ uploadCounterTotal }}</span>
            </template>
            <span v-else>Feltöltve: {{ uploadedFilesCount }}/{{ uploadCounterTotal }}</span>
          </div>
          <div class="actions-controls">
            <model-select
                class="uploader-select"
                :options="listByCompany"
                v-model="selectedTag"
                @input="selected"
                placeholder="Tömeges címkék"
                style="margin-top: -2px !important;"
            />
            <b-button variant="outline-secondary" class="circle-btn"
                      style="padding-left: 12px; padding-right: 15px; width: 40px; height: 40px; background-color: white; border: 1px solid #AEC0C7 !important; box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;"
                      @click="deleteAll">
              <i class="fal fa-trash-can-xmark"></i>
            </b-button>
            <b-button variant="success" class="circle-btn"
                      style="width: 50px; height: 50px; border: 1px solid #AEC0C7 !important;"
                      :disabled="isUploading" @click="uploadFilesSequentially">
              <i class="fal fa-upload"></i>
            </b-button>
          </div>
        </div>


        <div v-for="(item, index) in files" :key="`${item.name}_${index}`" class="file-row">
          <div
              class="file-name mb-2"
              :class="{
              'file-name-duplicate': item.alreadyUploaded || item.status === 'failed',
              'file-name-success': item.status === 'success'
            }"
              :style="fileNameStyle(item)"
          >
            <span class="name-text">{{ item.name }}</span>
            <div class="file-name-actions">
            <span :key="`${item.status}_${item.isUploading}_${item.progress}`" class="status-inline px-2">
              <template v-if="item.isUploading === true && item.status === 'uploading' && item.progress < 100">
                <i class="fal fa-spinner-third fa-spin mr-2"></i>
                Folyamatban
              </template>
              <template v-else-if="item.status === 'success'">Sikeres</template>
              <template v-else-if="item.alreadyUploaded"></template>
              <template v-else-if="item.status === 'failed'">Sikertelen</template>
            </span>
              <span v-if="item.errorMessage" class="error-message px-2">{{ item.errorMessage }}</span>
              <b-button variant="link" class="delete-inline" @click="deleteFile(index)">
                <i class="fal fa-trash-can-xmark"></i>
              </b-button>
            </div>
          </div>
          <model-select
              class="uploader-select row-select"
              :options="listByCompany"
              v-model="item.listByCompany"
              :is-disabled="isRowSelectDisabled(item)"
              @input="selected"
              placeholder="Címke megadása"
          />

        </div>
      </div>

  </div>
</template>

<script>
import Api from "api-client";
import {mapGetters} from "vuex";
import {ModelSelect} from "vue-search-select";
import "vue-search-select/dist/VueSearchSelect.css";
import PageHeader from "@/components/PageHeader.vue";

export default {
  name: "FileUpload",
  components: {
    PageHeader,
    ModelSelect,
  },
  data() {
    const acceptedFileExtensions = [
      ".jpeg",
      ".png",
      ".jpg",
      ".jfif",
      ".tif",
      ".tiff",
      ".heic",
      ".bmp",
      ".pdf",
      ".doc",
      ".docx",
      ".rtf",
      ".txt",
      ".csv",
      ".xlsx",
      ".xls",
      ".xml",
      ".zip",
      ".7z",
    ];

    return {
      files: [],
      selectedTag: null,
      listByCompany: [],
      tableKey: 0,
      isUploading: false,
      uploadStarted: false,
      uploadCounterTotal: 0,
      resourceType: null,
      showUploadFormatsCard: true,
      acceptedFileExtensions: acceptedFileExtensions.join(","),
      acceptedFileExtensionSet: new Set(acceptedFileExtensions),
    };
  },
  computed: {
    ...mapGetters("app", ["getActiveCompany"]),
    pageTitle() {
      const titlesByResourceType = {
        EXPENSE: "Költségek",
        INCOME: "Bevételek",
        CASH_FLOW: "Pénzforgalom",
        WAGE: "Bér",
      };

      return titlesByResourceType[this.resourceType] || "";
    },
    showUploadCounter() {
      return this.uploadStarted && this.uploadCounterTotal > 0;
    },
    hasUploadFinished() {
      return this.showUploadCounter && !this.isUploading;
    },
    uploadedFilesCount() {
      return this.files.filter((item) => item.status === "success").length;
    },
  },
  watch: {
    selectedTag: function (val) {
      this.files = this.files.map((f) => {
        f.listByCompany = val;
        return f;
      });
      this.$nextTick(() => {
        this.tableKey += 1;
      });
    },
  },
  methods: {
    selected() {
      this.$nextTick(() => {
        this.tableKey += 1;
      });
    },
    isRowSelectDisabled(item) {
      return ["uploading", "success", "failed"].includes(item.status) || item.isUploading === true;
    },
    isFileUploadable(item) {
      return item.status !== "success" && !item.alreadyUploaded;
    },
    getFileExtension(fileName) {
      const lastDotIndex = fileName.lastIndexOf(".");
      return lastDotIndex === -1 ? "" : fileName.slice(lastDotIndex).toLowerCase();
    },
    isAcceptedFile(file) {
      return this.acceptedFileExtensionSet.has(this.getFileExtension(file.name));
    },
    addFileIfAccepted(file) {
      if (!this.isAcceptedFile(file)) return;

      const fileItem = {
        file,
        name: file.name,
        listByCompany: null,
        alreadyUploaded: false,
        status: "pending",
        isUploading: false,
        progress: 0,
        errorMessage: "",
      };
      this.files.push(fileItem);
      this.checkIfAlreadyUploaded(file, fileItem);
    },
    handleDroppedFiles(e) {
      const droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      [...droppedFiles].forEach(this.addFileIfAccepted);
    },
    fileUploaderChange(e) {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length) {
        [...selectedFiles].forEach(this.addFileIfAccepted);
      }
      e.target.value = "";
    },
    async sha256FromFile(file) {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
      return Array.from(new Uint8Array(hashBuffer))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
    },
    async checkIfAlreadyUploaded(file, fileItem) {
      if (!this.getActiveCompany?.id) return;

      try {
        const hash = await this.sha256FromFile(file);
        const payload = {
          companyId: this.getActiveCompany.id,
          size: file.size,
          name: file.name,
          hash,
        };

        const resp = await Api.checkResourceUpload(payload);
        const currentIndex = this.files.indexOf(fileItem);
        if (currentIndex === -1) return;

        this.$set(this.files[currentIndex], "alreadyUploaded", !!resp?.data?.alreadyUploaded);
        if (resp?.data?.alreadyUploaded) {
          this.$set(this.files[currentIndex], "status", "failed");
          this.$set(this.files[currentIndex], "isUploading", false);
          this.$set(this.files[currentIndex], "errorMessage", "A fájl már fel van töltve");
          this.moveAlreadyUploadedFilesToTop();
        }
      } catch (e) {
        const currentIndex = this.files.indexOf(fileItem);
        if (currentIndex !== -1) {
          this.$set(this.files[currentIndex], "alreadyUploaded", false);
        }
      }
    },
    moveAlreadyUploadedFilesToTop() {
      this.files = [
        ...this.files.filter((item) => item.alreadyUploaded),
        ...this.files.filter((item) => !item.alreadyUploaded),
      ];
    },
    fileNameStyle(item) {
      const progress = Math.max(0, Math.min(100, item.progress || 0));
      let color = "#c8d6df";
      if (item.status === "success") {
        color = "#D5F3AB";
      } else if (item.status === "uploading") {
        color = "#D5F3AB";
      } else if (item.status === "failed" || item.alreadyUploaded) {
        color = "#F4BBB8";
      }

      return {
        background: `linear-gradient(to right, ${color} ${progress}%, #c8d6df ${progress}%)`,
      };
    },
    getParentFolderId() {
      const folderIdsByResourceType = {
        EXPENSE: this.getActiveCompany?.expensesFolderId,
        INCOME: this.getActiveCompany?.incomesFolderId,
        CASH_FLOW: this.getActiveCompany?.cashFlowFolderId,
        WAGE: this.getActiveCompany?.fixEmployeesFolderId,
      };

      return folderIdsByResourceType[this.resourceType] || this.getActiveCompany?.expensesFolderId;
    },
    buildUploadFormData(item) {
      const fd = new FormData();

      fd.set("file", item.file, item.name.trim());
      if (item.listByCompany) {
        fd.set("tags", [item.listByCompany]);
      } else if (this.selectedTag) {
        fd.set("tags", [this.selectedTag]);
      }
      fd.set("parentId", this.getParentFolderId());
      fd.set("fromPwa", true);
      fd.set("companyId", this.getActiveCompany.id);
      if (this.resourceType) {
        fd.set("resourceType", this.resourceType);
      }
      if (this.resourceType === "WAGE" && this.$route.query.employeeId) {
        fd.set("employeeId", this.$route.query.employeeId);
      }
      return fd;
    },
    getErrorMessage(err) {
      if (!err?.response?.data) {
        return "Sikertelen feltöltés";
      }

      if (err.response.data.message) {
        return err.response.data.message;
      }

      if (err.response.data.detailedMessage) {
        return this.$cte(err.response.data.detailedMessage);
      }

      return "Sikertelen feltöltés";
    },
    getAvailableTagsResourceType() {
      const availableTagsResourceTypesByResourceType = {
        INCOME: "INCOME",
        EXPENSE: "COST",
        CASH_FLOW: "CASH_FLOW",
        WAGE: "WAGE",
      };

      return availableTagsResourceTypesByResourceType[this.resourceType] || this.resourceType;
    },
    async uploadFilesSequentially() {
      if (this.isUploading || !this.files.length) return;

      const uploadableFiles = this.files.filter(this.isFileUploadable);
      if (!uploadableFiles.length) return;

      this.uploadStarted = true;
      this.uploadCounterTotal = this.files.length;
      this.isUploading = true;

      for (let i = 0; i < this.files.length; i += 1) {
        const item = this.files[i];

        if (!this.isFileUploadable(item)) {
          if (item.alreadyUploaded) {
            this.$set(item, "status", "failed");
            this.$set(item, "isUploading", false);
            this.$set(item, "progress", 100);
            if (!item.errorMessage) {
              this.$set(item, "errorMessage", "A fájl már fel van töltve");
            }
          }
          continue;
        }

        this.$set(item, "status", "uploading");
        this.$set(item, "isUploading", true);
        this.$set(item, "errorMessage", "");
        this.$set(item, "progress", 20);

        try {
          const fd = this.buildUploadFormData(item);
          this.$set(item, "progress", 60);
          await Api.uploadResource(fd);
          this.$set(this.files, i, {
            ...item,
            progress: 100,
            isUploading: false,
            status: "success",
          });
        } catch (err) {
          this.$set(this.files, i, {
            ...item,
            progress: 100,
            isUploading: false,
            status: "failed",
            errorMessage: this.getErrorMessage(err),
          });
        }
      }

      this.isUploading = false;
    },
    deleteFile(index) {
      this.files.splice(index, 1);
      if (this.files.length === 0) {
        this.uploadStarted = false;
        this.uploadCounterTotal = 0;
      }
    },
    deleteAll() {
      if (confirm("Minden fájlt törölni szeretne?")) {
        this.files = [];
        this.uploadStarted = false;
        this.uploadCounterTotal = 0;
      }
    },
    getListByCompany() {
      if (!this.getActiveCompany?.id) return;

      Api.getAvailableTags(this.getActiveCompany.id, this.getAvailableTagsResourceType()).then((resp) => {
        this.listByCompany = [];

        resp.data.forEach((data) => {
          if (!data.isDateRelated) {
            this.listByCompany.push({
              value: data.id,
              text: data.name,
            });
          }
        });

        this.listByCompany.unshift({
          value: [],
          text: "Nincs címke",
        });
      });
    },
    isUploadFormatsCardHidden() {
      if (typeof document === "undefined") return false;

      return document.cookie
          .split(";")
          .some((cookie) => cookie.trim() === "fileUploadFormatsCardHidden=true");
    },
    hideUploadFormatsCard() {
      this.showUploadFormatsCard = false;

      if (typeof document === "undefined") return;

      const expires = new Date();
      expires.setMonth(expires.getMonth() + 2);
      document.cookie = `fileUploadFormatsCardHidden=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    },
  },
  created() {
    this.showUploadFormatsCard = !this.isUploadFormatsCardHidden();
    this.resourceType = this.$route.query.resourceType || null;
    this.getListByCompany();
  },
};
</script>

<style scoped>
.upload-container {
  padding: 0;
}

.upload-formats-card {
  position: relative;
  margin: 22px 0px;
  padding: 28px 28px;
  min-height: 220px;
  border: 1px solid #aec0c7;
  border-radius: 32px;
  background: #D6DDE2;
  color: #252734;
  box-sizing: border-box;
}

.upload-formats-title {
  margin-bottom: 28px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 700;
}

.upload-formats-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 64px;
  max-width: 900px;
}

.upload-formats-column {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.upload-formats-label {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: 700;
}

.upload-formats-values {
  font-size: 14px;
  line-height: 1.25;
}

.upload-formats-close {
  position: absolute;
  top: 28px;
  right: 40px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #000000;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.upload-formats-info {
  position: absolute;
  right: 32px;
  bottom: 28px;
  width: 32px;
  height: 32px;
  border: 1px solid #000000;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  color: #000000;
  text-decoration: none;
  box-shadow: 0 0 0 3px #ffffff;
}

.upload-formats-info:hover,
.upload-formats-info:focus {
  color: #000000;
  text-decoration: none;
}

.file-upload-page {
  margin: 16px 0;
  padding: 0;
}

.file-upload-page-empty {
  box-sizing: border-box;
}

.drop-area {
  min-height: 140px;
  border: 1px dashed #9aa1a9;
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between !important;
  gap: 40px;
  padding: 40px 40px;
  cursor: pointer;
}

.drop-area-empty {
  box-sizing: border-box;
}

.drop-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: left;
}

.drop-icon {
  flex: 0 0 auto;
  font-size: 28px;
  margin: 0;
  color: #000000;
}

.drop-title {
  font-size: 18px;
  line-height: 0.2;
  color: #252734;
}

.drop-subtitle {
  font-size: 14px;
  line-height: 1.1;
  color: #aec0c6;
}

.pick-btn {
  flex: 0 0 25%;
  width: 25%;
  min-width: 0;
  border-radius: 32px;
  padding: 12px 24px;
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  color: #252734 !important;
  border-color: #aebfc7 !important;
  background: #ffffff !important;
}

.file-list-wrapper {
  border: 1px solid #cdd7dd;
  border-radius: 32px;
  margin-top: 24px;
  padding: 24px;
}

.actions-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.upload-counter {
  flex: 0 0 auto;
  margin-right: auto;
  min-height: 40px;
  padding: 0 24px;
  border: 1px solid #aebfc7;
  border-radius: 32px;
  background: #ffffff;
  color: #252734;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
}

.upload-counter-complete {
  padding-left: 28px;
  gap: 0;
}

.upload-counter-loader {
  flex: 0 0 auto;
  color: #252734;
  font-size: 16px;
  line-height: 1;
}

.actions-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.uploader-select {
  min-width: 280px !important;
  width: 28px !important;
  margin-bottom: 0;
}

.uploader-select::v-deep.ui.selection.dropdown {
  min-height: 40px;
  height: 40px;
  border-radius: 32px !important;
  background: #ffffff !important;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  padding: 0 40px 0 28px !important;
}

.uploader-select::v-deep.ui.selection.dropdown > .text,
.uploader-select::v-deep.ui.selection.dropdown > .default.text {
  color: #252734 !important;
  font-size: 16px;
  line-height: 22px;
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
}

.uploader-select::v-deep.ui.selection.dropdown > input.search {
  top: 0 !important;
  height: 40px !important;
  line-height: 40px !important;
  padding: 0 40px 0 28px !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 32px !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #252734 !important;
  font-size: 16px !important;
}

.uploader-select::v-deep.ui.selection.dropdown > input.search::placeholder {
  line-height: 40px;
  padding-top: 5px !important;
}

.uploader-select::v-deep.ui.selection.dropdown > .dropdown.icon {
  top: 50% !important;
  right: 26px !important;
  transform: translateY(-50%);
  color: #aebfc7 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.uploader-select::v-deep.ui.selection.dropdown > .dropdown.icon::before {
  margin: 0 !important;
}

.uploader-select::v-deep.ui.selection.active.dropdown,
.uploader-select::v-deep.ui.selection.active.dropdown:hover,
.uploader-select::v-deep.ui.selection.dropdown:focus,
.uploader-select::v-deep.ui.selection.dropdown:hover {
  border-color: #aebfc7 !important;
  box-shadow: none !important;
}

.uploader-select::v-deep.ui.selection.dropdown .menu {
  border: 1px solid #aebfc7 !important;
  border-radius: 20px !important;
  margin-top: 8px !important;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
}

.file-row {
  display: grid;
  grid-template-columns: 1fr 230px 36px;
  gap: 12px;
  align-items: center;
}

.file-name {
  background: #c8d6df;
  border: 1px solid #aebfc7;
  border-radius: 28px;
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  font-size: 14px;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
}

.file-name-duplicate {
  color: #ef6b6b;
  border-color: #ef6b6b;
}

.file-name-success {
  border-color: #a7c98a;
}

.name-text {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-name-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: max-content;
}

.status-inline {
  flex: 0 0 auto;
  font-size: 14px;
  white-space: nowrap;
}

.error-message {
  flex: 0 0 auto;
  font-size: 13px;
  color: #d95c5c;
  white-space: nowrap;
}

.row-select {
  width: 100%;
}

.circle-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  padding: 0;
}

.delete-inline {
  flex: 0 0 auto;
  color: #252734;
  padding: 0;
}

@media (max-width: 991px) {
  .upload-container {
    padding: 16px;
    border: 1px solid #cdd7dd;
    border-radius: 32px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }

  .upload-formats-card {
    margin: 0 0 16px;
    padding: 24px 24px 72px;
    min-height: auto;
    border-radius: 20px;
  }

  .upload-formats-title {
    margin-bottom: 24px;
    padding-right: 36px;
    font-size: 20px;
  }

  .upload-formats-content {
    grid-template-columns: 1fr;
    row-gap: 22px;
  }

  .upload-formats-label {
    font-size: 18px;
  }

  .upload-formats-values {
    font-size: 16px;
  }

  .upload-formats-close {
    top: 24px;
    right: 24px;
    font-size: 22px;
  }

  .upload-formats-info {
    right: 19px;
    bottom: 24px;
    width: 32px;
    height: 32px;
    font-size: 16px;
  }

  .file-upload-page {
    padding: 0;
  }

  .drop-area {
    min-height: 0;
    border: none;
    border-radius: 0;
    display: block;
    padding: 0;
  }

  .drop-icon,
  .drop-content {
    display: none;
  }

  .pick-btn,
  .drop-area-empty .pick-btn {
    display: block;
    width: 100%;
    min-width: 0;
    padding: 12px 24px;
    margin: 0;
    font-size: 16px;
  }

  .file-list-wrapper {
    border: none;
    padding: 0;
  }

  .actions-row {
    width: 100%;
    gap: 12px;
  }

  .upload-counter {
    padding: 0 16px;
    font-size: 12px;
  }

  .actions-controls {
    width: auto;
    flex-wrap: nowrap;
    justify-content: flex-end;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
  }

  .actions-controls .uploader-select {
    flex: 1 1 auto;
    width: auto !important;
    min-width: 0 !important;
    margin-bottom: 0;
  }

  .file-row {
    grid-template-columns: minmax(0, 1fr);
    margin-bottom: 32px;
  }

  .file-name {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .file-row .uploader-select {
    width: 100% !important;
    min-width: 0 !important;
  }
}
</style>
